import React, { createContext, forwardRef, useContext, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import ListSubheader from '@mui/material/ListSubheader';
import Typography from '@mui/material/Typography';
import { ListChildComponentProps, VariableSizeList } from 'react-window';

/**
 * A virtualized listbox slot for MUI Autocomplete.
 *
 * The criteria selects are fed whole reference tables — every domain, every landing page —
 * so the popup has to render a window, not the whole list. MUI's own virtualization recipe:
 * `renderOption` and `renderGroup` hand back plain data instead of elements, and this
 * component turns that data into rows react-window can size and recycle.
 *
 * Consumers must therefore pass:
 *   renderOption={(props, option) => [props, option] as React.ReactNode}
 *   renderGroup={(params) => params as unknown as React.ReactNode}
 */

const LISTBOX_PADDING = 8;
const OPTION_HEIGHT = 36;
const GROUP_HEADER_HEIGHT = 40;
const MAX_LIST_HEIGHT = 300;

// Autocomplete styles its options from the listbox element it normally renders. Replacing that
// element drops those styles with it, so the rows carry their own.
const OPTION_SX = {
  display: 'flex',
  alignItems: 'center',
  boxSizing: 'border-box',
  overflow: 'hidden',
  px: 2,
  cursor: 'pointer',
  outline: 0,
  '&.Mui-focused': { bgcolor: 'action.hover' },
  '&[aria-selected="true"]': { bgcolor: 'action.selected' },
  '&[aria-disabled="true"]': { opacity: 0.38, pointerEvents: 'none' },
} as const;

const GROUP_HEADER_SX = {
  px: 2,
  lineHeight: `${GROUP_HEADER_HEIGHT}px`,
  color: 'text.secondary',
  bgcolor: 'transparent',
} as const;

type OptionLike = { label: string; note?: string };
type GroupRow = { key: string | number; group: string; children?: React.ReactNode[] };
type OptionRow = [React.HTMLAttributes<HTMLLIElement> & { key?: React.Key }, OptionLike];
export type ListboxRow = GroupRow | OptionRow;

const isGroupRow = (row: ListboxRow): row is GroupRow => !Array.isArray(row);

function renderRow({ data, index, style }: ListChildComponentProps<ListboxRow[]>) {
  const row = data[index];
  if (!row) return null;

  // react-window positions rows absolutely from 0; shift them past the listbox's own padding.
  const inlineStyle = { ...style, top: (style.top as number) + LISTBOX_PADDING };

  if (isGroupRow(row)) {
    return (
      <ListSubheader component='div' style={inlineStyle} sx={GROUP_HEADER_SX}>
        {row.group}
      </ListSubheader>
    );
  }

  const [optionProps, option] = row;
  const { key: _key, ...rest } = optionProps;
  return (
    <Typography component='li' {...rest} noWrap style={inlineStyle} sx={OPTION_SX}>
      {option.note ? (
        // The label gives way (ellipsis) so the note stays readable at any width.
        <>
          <Box component='span' sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {option.label}
          </Box>
          <Typography component='span' variant='caption' color='text.secondary' sx={{ ml: 1, flexShrink: 0 }}>
            {option.note}
          </Typography>
        </>
      ) : (
        option.label
      )}
    </Typography>
  );
}

// react-window owns the scroll container, so the props MUI meant for the listbox element
// (role, aria wiring, className) have to be handed down to it rather than to our wrapper.
const OuterElementContext = createContext<React.HTMLAttributes<HTMLElement>>({});

const OuterElement = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  function OuterElement(props, ref) {
    const outerProps = useContext(OuterElementContext);
    // Both sides listen for scroll: react-window to render the rows scrolled into view, MUI to
    // tell a touch scroll from a tap. Letting MUI's handler replace react-window's leaves the
    // list stuck on its first window of rows while the scrollbar moves over blank space.
    const onScroll = (event: React.UIEvent<HTMLDivElement>) => {
      props.onScroll?.(event);
      outerProps.onScroll?.(event);
    };
    return <div ref={ref} {...props} {...outerProps} onScroll={onScroll} />;
  }
);

export const VirtualizedListbox = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(
  function VirtualizedListbox({ children, ...other }, ref) {
    const rows: ListboxRow[] = [];
    (children as ListboxRow[] | undefined)?.forEach((child) => {
      rows.push(child);
      if (isGroupRow(child) && child.children) {
        rows.push(...(child.children as unknown as ListboxRow[]));
      }
    });

    const listRef = useRef<VariableSizeList>(null);
    useEffect(() => {
      listRef.current?.resetAfterIndex(0, true);
    }, [rows.length]);

    const itemSize = (index: number) => {
      const row = rows[index];
      return row && isGroupRow(row) ? GROUP_HEADER_HEIGHT : OPTION_HEIGHT;
    };
    const contentHeight = rows.reduce((sum, _row, index) => sum + itemSize(index), 0);

    return (
      // react-window nests its rows in a <ul> that would otherwise keep a list's default padding.
      <Box ref={ref} sx={{ '& ul': { p: 0, m: 0, listStyle: 'none' } }}>
        <OuterElementContext.Provider value={other}>
          <VariableSizeList
            ref={listRef}
            itemData={rows}
            itemCount={rows.length}
            itemSize={itemSize}
            height={Math.min(MAX_LIST_HEIGHT, contentHeight) + 2 * LISTBOX_PADDING}
            width='100%'
            outerElementType={OuterElement}
            innerElementType='ul'
            overscanCount={5}
          >
            {renderRow}
          </VariableSizeList>
        </OuterElementContext.Provider>
      </Box>
    );
  }
);
